type ThrownResponse = Readonly<{
  type: "DataWithResponseInit";
  data: unknown;
  init: Readonly<{ status?: number; statusText?: string }> | null;
}>;

/**
 * Whether a failure is a `data(…, status)` route response as `data()` builds
 * it: what `loaderQuery` throws, and what a deferred loader promise read with
 * `use()` rejects with. React Router does not export the class.
 */
const isThrownResponse = (error: unknown): error is ThrownResponse =>
  typeof error === "object" &&
  error !== null &&
  (error as { type?: unknown }).type === "DataWithResponseInit";

/**
 * The status of a failure `loaderQuery` threw: the `data(…, status)` route
 * response `apolloDataInvariant` classifies a whole-operation error as (403,
 * 404 or 500). Undefined for anything that is not such a response.
 */
export const loaderQueryFailureStatus = (error: unknown): number | undefined =>
  isThrownResponse(error) ? error.init?.status : undefined;

/**
 * What a failure is about, for a report: the error a `data(error, 500)`
 * response wraps, else the failure itself. Reporting the response would
 * report a plain object, with no message or stack to group it by.
 */
export const loaderQueryFailureCause = (error: unknown): unknown =>
  isThrownResponse(error) && error.data instanceof Error ? error.data : error;

/**
 * A `data(…, status)` response as React Router's boundary would have read it
 * had a loader thrown it: an ErrorResponse. One thrown from render — a deferred
 * loader promise read with `use()` — reaches the boundary as the response
 * itself. Anything else is returned as it is.
 */
export const asRouteErrorResponse = (error: unknown): unknown =>
  isThrownResponse(error)
    ? {
        status: error.init?.status ?? 500,
        statusText: error.init?.statusText ?? "",
        internal: false,
        data: error.data,
      }
    : error;

/**
 * Whether the Apollo cache's copy of a loader read may stand in for the read
 * when it fails: not after a denial or a missing entity, which say the copy is
 * no longer the viewer's to see.
 */
export const mayServeCachedCopy = (error: unknown): boolean => {
  const status = loaderQueryFailureStatus(error);
  return status !== 403 && status !== 404;
};
